import { APP } from '@/constants/app'
import { emailJsConfig } from '@/constants/emailjs'
import { contactTimes, services } from '@/data/contact'
import { validateContactForm, type ContactFormData } from '@/utils/contactForm'

export async function sendContactEmail(data: ContactFormData): Promise<void> {
  if (Object.keys(validateContactForm(data)).length > 0) {
    throw new Error('Please check your contact details and correct any errors before sending.')
  }

  if (!emailJsConfig.serviceId || !emailJsConfig.templateId || !emailJsConfig.publicKey) {
    throw new Error(
      `The contact form is not configured yet. Please email ${APP.CONTACT_EMAIL} directly.`,
    )
  }

  const service = services
    .find((option) => option.value !== '' && option.value === data.service.trim())
    ?.label.trim()
  const preferredTime = contactTimes
    .find((option) => option.value === data.preferredTime.trim())
    ?.label.trim()

  if (!service || !preferredTime) {
    throw new Error(
      'Please select a service and a preferred contact time from the available options.',
    )
  }

  const signal = AbortSignal.timeout(15_000)
  let response: Response

  try {
    response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal,
      body: JSON.stringify({
        service_id: emailJsConfig.serviceId,
        template_id: emailJsConfig.templateId,
        user_id: emailJsConfig.publicKey,
        template_params: {
          from_name: `${data.firstName.trim()} ${data.lastName.trim()}`,
          from_email: data.email.trim(),
          reply_to: data.email.trim(),
          phone: data.phone.trim() || 'Not provided',
          service,
          interest: service,
          preferred_time: preferredTime,
          subject: `New enquiry: ${service}`,
          message: data.message.trim(),
        },
      }),
    })
  } catch {
    if (signal.aborted) {
      throw new Error(
        `Sending your enquiry timed out. Delivery could not be confirmed; please email ${APP.CONTACT_EMAIL} before resubmitting.`,
      )
    }

    throw new Error(
      `We could not confirm your enquiry was sent. Check your internet connection, or email ${APP.CONTACT_EMAIL} for help.`,
    )
  }

  if (!response.ok) {
    throw new Error(
      response.status === 429
        ? `Too many enquiries have been sent. Please wait a few minutes before trying again, or email ${APP.CONTACT_EMAIL} directly.`
        : `We could not send your enquiry. Please try again later or email ${APP.CONTACT_EMAIL} directly.`,
    )
  }
}
